import { useEffect, useState } from 'react';
import { IndirectTaxService } from '../../services/indirectTax.service';
import { TaxApplicability, TaxCodeLookup, TaxCodeLookupItem } from '../../interfaces/indirectTax.interface';

/**
 * Loads the tax-code picker list for an institution. Failure (module not enabled, no permission) is
 * swallowed: the caller then simply has no codes and keeps the plain free-rate input.
 */
export function useTaxCodeLookup(institutionId: string | null | undefined): TaxCodeLookup | null {
  const [lookup, setLookup] = useState<TaxCodeLookup | null>(null);
  useEffect(() => {
    if (!institutionId) return;
    let cancelled = false;
    Promise.resolve(IndirectTaxService.lookup(institutionId))
      .then(l => { if (!cancelled) setLookup(l ?? null); })
      .catch(() => { if (!cancelled) setLookup(null); });
    return () => { cancelled = true; };
  }, [institutionId]);
  return lookup;
}

/** Codes selectable on a document of the given side; inactive ones only when they are the current value. */
export function selectableCodes(
  lookup: TaxCodeLookup | null,
  side: 'OUTPUT' | 'INPUT',
  currentId?: string | null,
): TaxCodeLookupItem[] {
  if (!lookup) return [];
  return (lookup.codes ?? []).filter(c =>
    (c.applicability === 'BOTH' || c.applicability === (side as TaxApplicability))
    && (c.active || c.id === currentId));
}

export const hasTaxCodes = (lookup: TaxCodeLookup | null): boolean => !!lookup && (lookup.codes ?? []).some(c => c.active);

/** Preview-only tax: net * effective rate, 2dp half-up. The saved document's figures come from the backend. */
export const previewTax = (net: number, ratePct: number): number =>
  Math.round((net * ratePct) / 100 * 100 + Number.EPSILON) / 100;

/** Display text for a line's tax code on read-only detail pages: the code, or the effective rate / dash. */
export function taxCodeLabel(lookup: TaxCodeLookup | null, taxCodeId?: string | null): string {
  if (!taxCodeId) return '—';
  const c = lookup?.codes?.find(x => x.id === taxCodeId);
  return c ? `${c.code} — ${c.summary || `${c.effectiveRatePct}%`}` : '—';
}
